import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface CommissionAuditRecord {
  user_id: number
  username: string
  email: string
  payment_amount: number
  payment_date: string
  expected_usdt_commission: number
  actual_usdt_commission: number
  expected_share_commission: number
  actual_share_commission: number
  status: 'complete' | 'incomplete' | 'missing'
  share_purchase_id: string
  phase_id: number
  phase_name: string
  sponsor_id?: number
  sponsor_username?: string
}

interface CommissionCorrection {
  user_id: number
  payment_amount: number
  usdt_commission: number
  share_commission: number
  correction_reason: string
}

const CommissionAuditDashboard: React.FC = () => {
  const [auditRecords, setAuditRecords] = useState<CommissionAuditRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null)
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    to: new Date().toISOString().split('T')[0] // today
  })
  const [filterUserId, setFilterUserId] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'complete' | 'incomplete' | 'missing'>('all')
  const [showCorrectionForm, setShowCorrectionForm] = useState(false)
  const [correctionData, setCorrectionData] = useState<CommissionCorrection>({
    user_id: 0,
    payment_amount: 0,
    usdt_commission: 0,
    share_commission: 0,
    correction_reason: ''
  })

  useEffect(() => {
    loadAuditData()
  }, [dateRange, filterUserId, filterStatus])

  const loadAuditData = async () => {
    setLoading(true)
    setMessage(null)

    try {
      // Fetch audit data via API
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'load_audit_data',
          data: { dateFrom: dateRange.from, dateTo: dateRange.to, filterUserId, filterStatus }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      const { purchases, commissions, referrals } = result.data

      // Process audit records
      const auditRecords: CommissionAuditRecord[] = (purchases || []).map((purchase: any) => {
        const referral = referrals?.find((r: any) => r.referred_id === purchase.user_id)

        // Get ALL commission transactions for this share purchase (there might be multiple)
        const relatedCommissions = commissions?.filter((c: any) => c.share_purchase_id === purchase.id) || []

        // Calculate expected commissions (15% for Pre Sale phase)
        const expectedUsdtCommission = purchase.total_amount * 0.15
        const expectedShareCommission = purchase.shares_purchased * 0.15

        // Sum up ALL actual commissions from all related transactions
        const actualUsdtCommission = relatedCommissions.reduce((sum: number, c: any) => sum + (parseFloat(c.usdt_commission) || 0), 0)
        const actualShareCommission = relatedCommissions.reduce((sum: number, c: any) => sum + (parseFloat(c.share_commission) || 0), 0)

        // Determine status based on total commissions
        let status: 'complete' | 'incomplete' | 'missing' = 'missing'
        if (relatedCommissions.length > 0) {
          // Use small tolerance for floating point comparison
          const usdtDiff = Math.abs(actualUsdtCommission - expectedUsdtCommission)
          const shareDiff = Math.abs(actualShareCommission - expectedShareCommission)

          if (usdtDiff < 0.01 && shareDiff < 0.001) {
            status = 'complete'
          } else {
            status = 'incomplete'
          }
        }

        // IMPORTANT: Only include records that have a referral relationship
        // If there's no referral, there should be no commission, so it's not an audit issue
        if (!referral) {
          return null
        }

        return {
          user_id: purchase.user_id,
          username: purchase.users.username,
          email: purchase.users.email,
          payment_amount: parseFloat(purchase.total_amount),
          payment_date: purchase.purchase_date || purchase.created_at,
          expected_usdt_commission: expectedUsdtCommission,
          actual_usdt_commission: actualUsdtCommission,
          expected_share_commission: expectedShareCommission,
          actual_share_commission: actualShareCommission,
          status,
          share_purchase_id: purchase.id,
          phase_id: purchase.phase_id,
          phase_name: purchase.investment_phases.phase_name,
          sponsor_id: referral?.referrer_id,
          sponsor_username: referral?.users?.username
        }
      }).filter((record: CommissionAuditRecord | null): record is CommissionAuditRecord => record !== null) // Remove null records (purchases without referrals)

      // Apply status filter
      const filteredRecords = filterStatus === 'all' 
        ? auditRecords 
        : auditRecords.filter(record => record.status === filterStatus)

      setAuditRecords(filteredRecords)

      const totalRecords = auditRecords.length
      const completeRecords = auditRecords.filter(r => r.status === 'complete').length
      const incompleteRecords = auditRecords.filter(r => r.status === 'incomplete').length
      const missingRecords = auditRecords.filter(r => r.status === 'missing').length

      setMessage({
        type: 'info',
        text: `Loaded ${totalRecords} records: ${completeRecords} complete, ${incompleteRecords} incomplete, ${missingRecords} missing commissions`
      })

    } catch (error) {
      console.error('Error loading audit data:', error)
      setMessage({
        type: 'error',
        text: `Error loading audit data: ${error instanceof Error ? error.message : String(error)}`
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCorrection = async (record: CommissionAuditRecord) => {
    // Auto-calculate missing commission amounts
    const missingUsdtCommission = record.expected_usdt_commission - record.actual_usdt_commission
    const missingShareCommission = record.expected_share_commission - record.actual_share_commission

    // Confirm before applying automatic correction
    const confirmMessage = `Auto-fix commission for ${record.username}?\n\n` +
      `Missing USDT: $${missingUsdtCommission.toFixed(2)}\n` +
      `Missing Shares: ${missingShareCommission.toFixed(4)}\n\n` +
      `This will automatically create the missing commission transaction.`

    if (!confirm(confirmMessage)) {
      return
    }

    // Apply automatic correction
    await applyAutomaticCorrection(record, missingUsdtCommission, missingShareCommission)
  }

  const applyAutomaticCorrection = async (record: CommissionAuditRecord, missingUsdtCommission: number, missingShareCommission: number) => {
    setLoading(true)
    try {
      // Get referrer via API
      const refResponse = await fetch(`${API_BASE_URL}/api/admin/commission-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_referrer', data: { userId: record.user_id } })
      })
      const refResult = await refResponse.json()
      if (!refResponse.ok || !refResult.data) throw new Error(`No referral found for user ${record.user_id}`)

      // Apply correction via API
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'apply_correction',
          data: {
            userId: record.user_id,
            referrerId: refResult.data.referrer_id,
            sharePurchaseId: record.share_purchase_id,
            paymentAmount: record.payment_amount,
            usdtCommission: missingUsdtCommission,
            shareCommission: missingShareCommission,
            phaseId: record.phase_id,
            paymentDate: record.payment_date,
            reason: `Auto-corrected missing commission for ${record.username}`
          }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      console.log(`✅ Auto-corrected commission for ${record.username}: $${missingUsdtCommission.toFixed(2)} USDT + ${missingShareCommission.toFixed(4)} shares`)

      setMessage({
        type: 'success',
        text: `✅ Commission auto-corrected for ${record.username}: $${missingUsdtCommission.toFixed(2)} USDT + ${missingShareCommission.toFixed(4)} shares`
      })

      await loadAuditData()

    } catch (error) {
      console.error('Error applying automatic correction:', error)
      setMessage({
        type: 'error',
        text: `Error auto-correcting commission: ${error instanceof Error ? error.message : String(error)}`
      })
    } finally {
      setLoading(false)
    }
  }

  const handleManualCorrection = async (record: CommissionAuditRecord) => {
    setCorrectionData({
      user_id: record.user_id,
      payment_amount: record.payment_amount,
      usdt_commission: record.expected_usdt_commission - record.actual_usdt_commission,
      share_commission: record.expected_share_commission - record.actual_share_commission,
      correction_reason: `Missing commission correction for ${record.username}`
    })
    setShowCorrectionForm(true)
  }

  const submitCorrection = async () => {
    if (!correctionData.correction_reason.trim()) {
      setMessage({ type: 'error', text: 'Correction reason is required' })
      return
    }

    setLoading(true)
    try {
      // Submit manual correction via API
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'submit_manual_correction',
          data: {
            userId: correctionData.user_id,
            paymentAmount: correctionData.payment_amount,
            usdtCommission: correctionData.usdt_commission,
            shareCommission: correctionData.share_commission,
            reason: correctionData.correction_reason
          }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      console.log(`[COMMISSION_CORRECTION] Admin: 4, Target: ${correctionData.user_id}, USDT: $${correctionData.usdt_commission}, Shares: ${correctionData.share_commission}, Reason: ${correctionData.correction_reason}, Timestamp: ${new Date().toISOString()}`)

      setMessage({
        type: 'success',
        text: `Commission correction applied successfully. Transaction ID: ${result.data.transaction.id}`
      })

      setShowCorrectionForm(false)
      setCorrectionData({
        user_id: 0,
        payment_amount: 0,
        usdt_commission: 0,
        share_commission: 0,
        correction_reason: ''
      })

      // Reload audit data
      await loadAuditData()

    } catch (error) {
      console.error('Error applying correction:', error)
      setMessage({
        type: 'error',
        text: `Error applying correction: ${error instanceof Error ? error.message : String(error)}`
      })
    } finally {
      setLoading(false)
    }
  }

  const correctAllMissingCommissions = async () => {
    if (!confirm('This will create commission transactions for ALL missing commissions. This action cannot be undone. Continue?')) {
      return
    }

    setLoading(true)
    try {
      // Get all missing commissions
      const missingCommissions = auditRecords.filter(record =>
        record.status === 'missing' &&
        record.expected_usdt_commission > 0
      )

      console.log(`Processing ${missingCommissions.length} missing commission transactions...`)

      // Prepare corrections for bulk API call
      const corrections = missingCommissions.map(record => ({
        userId: record.user_id,
        sharePurchaseId: record.share_purchase_id,
        paymentAmount: record.payment_amount,
        usdtCommission: record.expected_usdt_commission,
        shareCommission: record.expected_share_commission,
        phaseId: 1,
        paymentDate: record.payment_date,
        username: record.username
      }))

      // Call bulk correction API
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'bulk_correct',
          data: { corrections }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setMessage({
        type: 'success',
        text: `Successfully corrected ${result.data.correctionCount} missing commissions out of ${result.data.total} total`
      })

      await loadAuditData()
    } catch (error: any) {
      console.error('Bulk commission correction error:', error)
      setMessage({
        type: 'error',
        text: `Bulk correction failed: ${error.message}`
      })
    } finally {
      setLoading(false)
    }
  }

  const correctIncompleteCommissions = async () => {
    if (!confirm('This will update commission transactions that are missing share commissions. Continue?')) {
      return
    }

    setLoading(true)
    try {
      // Get all incomplete commissions (missing share commissions)
      const incompleteCommissions = auditRecords.filter(record =>
        record.status === 'incomplete' &&
        (record.expected_share_commission - record.actual_share_commission) > 0
      )

      console.log(`Processing ${incompleteCommissions.length} incomplete commission transactions...`)

      // Prepare corrections for bulk API call
      const corrections = incompleteCommissions.map(record => ({
        userId: record.user_id,
        sharePurchaseId: record.share_purchase_id,
        expectedShareCommission: record.expected_share_commission,
        actualShareCommission: record.actual_share_commission,
        username: record.username
      }))

      // Call bulk correction API
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'bulk_correct_incomplete',
          data: { corrections }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setMessage({
        type: 'success',
        text: `Successfully corrected ${result.data.correctionCount} incomplete commissions out of ${result.data.total} total`
      })

      await loadAuditData()
    } catch (error: any) {
      console.error('Incomplete commission correction error:', error)
      setMessage({
        type: 'error',
        text: `Incomplete correction failed: ${error.message}`
      })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'text-green-600 bg-green-100'
      case 'incomplete': return 'text-yellow-600 bg-yellow-100'
      case 'missing': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Commission Audit Dashboard</h2>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">User ID (Optional)</label>
            <input
              type="number"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              placeholder="Filter by User ID"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status Filter</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="complete">Complete</option>
              <option value="incomplete">Incomplete</option>
              <option value="missing">Missing</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={loadAuditData}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh Audit Data'}
          </button>

          <button
            onClick={correctAllMissingCommissions}
            disabled={loading || auditRecords.filter(r => r.status === 'missing').length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            Fix All Missing Commissions ({auditRecords.filter(r => r.status === 'missing').length})
          </button>

          <button
            onClick={correctIncompleteCommissions}
            disabled={loading || auditRecords.filter(r => r.status === 'incomplete').length === 0}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
          >
            Fix Incomplete Commissions ({auditRecords.filter(r => r.status === 'incomplete').length})
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-4 rounded-md ${
          message.type === 'success' ? 'bg-green-100 text-green-700' :
          message.type === 'error' ? 'bg-red-100 text-red-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Audit Records Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Payment</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">USDT Commission</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Share Commission</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Sponsor</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {auditRecords.map((record, index) => (
              <tr key={`${record.user_id}-${record.share_purchase_id}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900" style={{ color: '#1f2937' }}>ID: {record.user_id}</div>
                  <div className="text-sm text-gray-700" style={{ color: '#374151' }}>{record.username}</div>
                  <div className="text-sm text-gray-600" style={{ color: '#4b5563' }}>{record.email}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900" style={{ color: '#1f2937' }}>${record.payment_amount.toFixed(2)}</div>
                  <div className="text-sm text-gray-700" style={{ color: '#374151' }}>{record.phase_name}</div>
                  <div className="text-sm text-gray-600" style={{ color: '#4b5563' }}>{new Date(record.payment_date).toLocaleDateString()}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900" style={{ color: '#1f2937' }}>
                    Expected: ${record.expected_usdt_commission.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-700" style={{ color: '#374151' }}>
                    Actual: ${record.actual_usdt_commission.toFixed(2)}
                  </div>
                  {record.expected_usdt_commission !== record.actual_usdt_commission && (
                    <div className="text-sm font-semibold text-red-600" style={{ color: '#dc2626' }}>
                      Missing: ${(record.expected_usdt_commission - record.actual_usdt_commission).toFixed(2)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900" style={{ color: '#1f2937' }}>
                    Expected: {record.expected_share_commission.toFixed(4)}
                  </div>
                  <div className="text-sm text-gray-700" style={{ color: '#374151' }}>
                    Actual: {record.actual_share_commission.toFixed(4)}
                  </div>
                  {record.expected_share_commission !== record.actual_share_commission && (
                    <div className="text-sm font-semibold text-red-600" style={{ color: '#dc2626' }}>
                      Missing: {(record.expected_share_commission - record.actual_share_commission).toFixed(4)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStatusColor(record.status)}`}>
                    {record.status.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {record.sponsor_username ? (
                    <div>
                      <div className="text-sm font-medium text-gray-900" style={{ color: '#1f2937' }}>ID: {record.sponsor_id}</div>
                      <div className="text-sm text-gray-700" style={{ color: '#374151' }}>{record.sponsor_username}</div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500" style={{ color: '#ffffff' }}>No Sponsor</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {record.status !== 'complete' && record.sponsor_username && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleCorrection(record)}
                        disabled={loading}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                        title="Automatically calculate and fix missing commission"
                      >
                        {loading ? 'Fixing...' : 'Auto-Fix Commission'}
                      </button>
                      <button
                        onClick={() => handleManualCorrection(record)}
                        disabled={loading}
                        className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 disabled:opacity-50"
                        title="Manually enter commission correction"
                      >
                        Manual Fix
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {auditRecords.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          No audit records found for the selected criteria.
        </div>
      )}

      {/* Correction Form Modal */}
      {showCorrectionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Commission Correction</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <input
                  type="number"
                  value={correctionData.user_id}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <input
                  type="number"
                  value={correctionData.payment_amount}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">USDT Commission to Add</label>
                <input
                  type="number"
                  step="0.01"
                  value={correctionData.usdt_commission}
                  onChange={(e) => setCorrectionData(prev => ({ ...prev, usdt_commission: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Share Commission to Add</label>
                <input
                  type="number"
                  step="0.01"
                  value={correctionData.share_commission}
                  onChange={(e) => setCorrectionData(prev => ({ ...prev, share_commission: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correction Reason</label>
                <textarea
                  value={correctionData.correction_reason}
                  onChange={(e) => setCorrectionData(prev => ({ ...prev, correction_reason: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Explain why this correction is needed..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCorrectionForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitCorrection}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Applying...' : 'Apply Correction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CommissionAuditDashboard
